import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { useBatchUsersQuery } from '../hooks/useBatchUsersQuery';
import {
  BreakdownDataCache,
  SceneDataCache,
  ElementDataCache,
  ElementRelationDataCache,
  ScheduleDataCache,
  SlotDataCache,
  CalDateDataCache,
  CacheUpdateMessage,
} from '../types/cache.types';
import { updateScene } from '../api/breakdown';
import { SceneWithUserNames, UpdateSceneParams } from '../types/scene.types';

/**
 * 统一的数据缓存 Context（专注于 /breakdown 页面）
 * 管理所有业务数据的缓存、订阅和跨标签页同步
 */

interface BreakdownDataCacheContextType {
  // Breakdown 相关
  getBreakdownCache: (breakdownId: string) => BreakdownDataCache | null;
  setBreakdownCache: (
    breakdownId: string,
    data: Partial<BreakdownDataCache>,
    options?: { skipAutoSceneSync?: boolean }
  ) => void;
  subscribeBreakdownCache: (breakdownId: string, callback: () => void) => () => void;
  clearBreakdownCache: (breakdownId: string) => void;

  // Scene 相关
  getSceneCache: (sceneId: string) => SceneDataCache | null;
  setSceneCache: (sceneId: string, data: Partial<SceneDataCache>) => void;
  subscribeSceneCache: (sceneId: string, callback: () => void) => () => void;
  clearSceneCache: (sceneId: string) => void;

  // Element 相关
  getElementCache: (breakdownId: string) => ElementDataCache | null;
  setElementCache: (breakdownId: string, data: Partial<ElementDataCache>) => void;
  subscribeElementCache: (breakdownId: string, callback: () => void) => () => void;
  clearElementCache: (breakdownId: string) => void;

  // ElementRelation 相关（场次元素与场次的关系）
  getElementRelationCache: (breakdownId: string) => ElementRelationDataCache | null;
  setElementRelationCache: (breakdownId: string, data: Partial<ElementRelationDataCache>) => void;
  subscribeElementRelationCache: (breakdownId: string, callback: () => void) => () => void;
  clearElementRelationCache: (breakdownId: string) => void;

  // Schedule 相关：拍摄计划列表（按分场 breakdownId）
  getScheduleCache: (breakdownId: string) => ScheduleDataCache | null;
  setScheduleCache: (breakdownId: string, data: Partial<ScheduleDataCache>) => void;
  subscribeScheduleCache: (breakdownId: string, callback: () => void) => () => void;
  clearScheduleCache: (breakdownId: string) => void;

  // Slot 相关
  getSlotCache: (scheduleId: string) => SlotDataCache | null;
  setSlotCache: (scheduleId: string, data: Partial<SlotDataCache>) => void;
  subscribeSlotCache: (scheduleId: string, callback: () => void) => () => void;
  clearSlotCache: (scheduleId: string) => void;

  // CalDate 相关
  getCalDateCache: (scheduleId: string) => CalDateDataCache | null;
  setCalDateCache: (scheduleId: string, data: Partial<CalDateDataCache>) => void;
  subscribeCalDateCache: (scheduleId: string, callback: () => void) => () => void;
  clearCalDateCache: (scheduleId: string) => void;

  // 通用方法
  clearAllCache: () => void;
}

const BreakdownDataCacheContext = createContext<BreakdownDataCacheContextType | undefined>(undefined);

export const useDataCache = () => {
  const context = useContext(BreakdownDataCacheContext);
  if (context === undefined) {
    throw new Error('useDataCache must be used within a BreakdownDataCacheProvider');
  }
  return context;
};

export const BreakdownDataCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { collectAndQueryUsers } = useBatchUsersQuery();
  
  // 缓存存储
  const breakdownCacheRef = useRef<Map<string, BreakdownDataCache>>(new Map());
  const sceneCacheRef = useRef<Map<string, SceneDataCache>>(new Map());
  const elementCacheRef = useRef<Map<string, ElementDataCache>>(new Map());
  const elementRelationCacheRef = useRef<Map<string, ElementRelationDataCache>>(new Map());
  const scheduleAggregateCacheRef = useRef<Map<string, ScheduleDataCache>>(new Map());
  const slotCacheRef = useRef<Map<string, SlotDataCache>>(new Map());
  const calDateCacheRef = useRef<Map<string, CalDateDataCache>>(new Map());

  // 订阅者存储
  const breakdownListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const sceneListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const elementListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const elementRelationListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const scheduleListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const slotListenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const calDateListenersRef = useRef<Map<string, Set<() => void>>>(new Map());

  // BroadcastChannel 用于跨标签页同步
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // 待同步到服务器的更新队列：Map<sceneId, { originalScene, updates, breakdownId }>
  const pendingSyncRef = useRef<Map<string, { originalScene: SceneWithUserNames; updates: UpdateSceneParams; breakdownId: string }>>(new Map());
  
  // 正在同步中的 sceneId 集合，避免重复同步
  const syncingSceneIdsRef = useRef<Set<string>>(new Set());

  // 通用的通知订阅者函数
  const notifyListeners = useCallback((listenersMap: Map<string, Set<() => void>>, id: string) => {
    const listeners = listenersMap.get(id);
    if (listeners && listeners.size > 0) {
      listeners.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error(`[BreakdownDataCache] 订阅者回调执行失败:`, error);
        }
      });
    }
  }, []);

  // 初始化 BroadcastChannel
  useEffect(() => {
    // 检查浏览器是否支持 BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        broadcastChannelRef.current = new BroadcastChannel('lineup-data-cache');

        // 监听其他标签页的消息
        broadcastChannelRef.current.onmessage = (event: MessageEvent<CacheUpdateMessage>) => {
          const message = event.data;

          // 根据消息类型通知对应的订阅者
          switch (message.cacheType) {
            case 'breakdown':
              notifyListeners(breakdownListenersRef.current, message.id);
              break;
            case 'scene':
              notifyListeners(sceneListenersRef.current, message.id);
              break;
            case 'element':
              notifyListeners(elementListenersRef.current, message.id);
              break;
            case 'elementRelation':
              notifyListeners(elementRelationListenersRef.current, message.id);
              break;
            case 'schedule':
              notifyListeners(scheduleListenersRef.current, message.id);
              break;
            case 'slot':
              notifyListeners(slotListenersRef.current, message.id);
              break;
            case 'calDate':
              notifyListeners(calDateListenersRef.current, message.id);
              break;
          }
        };
      } catch (error) {
        console.warn('[BreakdownDataCache] BroadcastChannel 初始化失败，跨标签页同步将不可用:', error);
      }
    } else {
      console.warn('[BreakdownDataCache] 浏览器不支持 BroadcastChannel，跨标签页同步将不可用');
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [notifyListeners]);

  // 通用的广播消息函数
  const broadcastMessage = useCallback((message: CacheUpdateMessage) => {
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage(message);
      } catch (error) {
        console.error('[BreakdownDataCache] 发送跨标签页消息失败:', error);
      }
    }
  }, []);

  // ==================== Breakdown 相关 ====================

  const getBreakdownCache = useCallback((breakdownId: string): BreakdownDataCache | null => {
    return breakdownCacheRef.current.get(breakdownId) || null;
  }, []);

  const setBreakdownCache = useCallback(
    (
      breakdownId: string,
      data: Partial<BreakdownDataCache>,
      options?: { skipAutoSceneSync?: boolean }
    ) => {
      const existingCache = breakdownCacheRef.current.get(breakdownId);
      const isNewCache = !existingCache;
      
      // 如果 data 中没有 breakdownInfo，保留 existingCache 中的 breakdownInfo（如果有）
      const breakdownInfo = data.breakdownInfo !== undefined 
        ? data.breakdownInfo 
        : (existingCache?.breakdownInfo ?? null);
      
      // 如果 data 中没有 scenes，保留 existingCache 中的 scenes（如果有）
      const scenes = data.scenes !== undefined
        ? data.scenes
        : (existingCache?.scenes ?? []);

      // 从 data 中排除 breakdownInfo 和 scenes，避免重复
      const { breakdownInfo: _, scenes: __, ...restData } = data;

      // 检测 scenes 数组的变化，识别需要同步到服务器的更新
      const pendingSceneUpdates: Array<{ sceneId: string; originalScene: SceneWithUserNames; updates: UpdateSceneParams }> = [];
      if (data.scenes !== undefined && existingCache && Array.isArray(existingCache.scenes) && Array.isArray(scenes)) {
        // 对比新旧 scenes，找出更新的 scene
        scenes.forEach((newScene) => {
          const oldScene = existingCache.scenes.find(s => s.sceneID === newScene.sceneID);
          if (oldScene) {
            // 检查是否有字段变化
            const updates: UpdateSceneParams = {};
            let hasChanges = false;
            
            // 对比所有字段
            Object.keys(newScene).forEach((key) => {
              const typedKey = key as keyof SceneWithUserNames;
              if (newScene[typedKey] !== oldScene[typedKey]) {
                (updates as any)[typedKey] = newScene[typedKey];
                hasChanges = true;
              }
            });
            
            if (hasChanges) {
              pendingSceneUpdates.push({
                sceneId: newScene.sceneID,
                originalScene: oldScene,
                updates,
              });
            }
          }
        });
      }
      
      const newCache: BreakdownDataCache = {
        ...existingCache, // 先保留现有缓存的其他字段
        breakdownInfo, // 用新的 breakdownInfo 覆盖
        scenes, // 用新的 scenes 覆盖（重要：必须在 existingCache 之后）
        timestamp: Date.now(),
        ...restData, // 用传入的其他数据覆盖
      };

      // 先更新 ref，确保订阅者读取时能获取到最新值
      breakdownCacheRef.current.set(breakdownId, newCache);

      // 通知本标签页的订阅者
      notifyListeners(breakdownListenersRef.current, breakdownId);

      // 广播到其他标签页
      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'breakdown',
        id: breakdownId,
        action: 'update',
        timestamp: Date.now(),
      });

      // 如果有更新的 scenes，保存到待同步队列并触发 API
      // 某些调用方（如批量更新流程）会自行完成服务器写入，这里应跳过自动单条同步
      if (!options?.skipAutoSceneSync && pendingSceneUpdates.length > 0) {
        pendingSceneUpdates.forEach(({ sceneId, originalScene, updates }) => {
          // 保存到待同步队列
          pendingSyncRef.current.set(sceneId, {
            originalScene,
            updates,
            breakdownId,
          });
          
          // 异步触发 API 请求（不阻塞缓存更新）
          if (!syncingSceneIdsRef.current.has(sceneId)) {
            syncingSceneIdsRef.current.add(sceneId);
            
            updateScene(sceneId, updates)
              .then(() => {
                // API 成功，从待同步队列移除
                pendingSyncRef.current.delete(sceneId);
              })
              .catch((error) => {
                console.error(`[BreakdownDataCache] 自动同步失败 (sceneId: ${sceneId}):`, error);
                // API 失败，回退缓存到更新前的值
                const pendingSync = pendingSyncRef.current.get(sceneId);
                if (pendingSync) {
                  const cache = breakdownCacheRef.current.get(breakdownId);
                  if (cache) {
                    const revertedScenes = cache.scenes.map((scene) =>
                      scene.sceneID === sceneId ? pendingSync.originalScene : scene
                    );
                    breakdownCacheRef.current.set(breakdownId, {
                      ...cache,
                      scenes: revertedScenes,
                      timestamp: Date.now(),
                    });
                    
                    // 通知订阅者缓存回退
                    notifyListeners(breakdownListenersRef.current, breakdownId);
                    broadcastMessage({
                      type: 'CACHE_UPDATE',
                      cacheType: 'breakdown',
                      id: breakdownId,
                      action: 'update',
                      timestamp: Date.now(),
                    });
                  }
                }
                // 从待同步队列移除
                pendingSyncRef.current.delete(sceneId);
              })
              .finally(() => {
                syncingSceneIdsRef.current.delete(sceneId);
              });
          }
        });
      }

      // 如果是新缓存，异步批量查询分场相关用户信息（不包含场次权限/用户）
      if (isNewCache) {
        collectAndQueryUsers(
          {
            breakdownInfo: newCache.breakdownInfo,
          },
          'Breakdown',
          [breakdownId]
        ).catch(error => {
          console.warn('批量查询分场表相关用户信息失败（不影响主流程）:', error);
        });
      }
    },
    [notifyListeners, broadcastMessage, collectAndQueryUsers]
  );

  const subscribeBreakdownCache = useCallback((breakdownId: string, callback: () => void) => {
    if (!breakdownListenersRef.current.has(breakdownId)) {
      breakdownListenersRef.current.set(breakdownId, new Set());
    }
    breakdownListenersRef.current.get(breakdownId)!.add(callback);

    // 返回取消订阅函数
    return () => {
      breakdownListenersRef.current.get(breakdownId)?.delete(callback);
    };
  }, []);

  const clearBreakdownCache = useCallback((breakdownId: string) => {
    breakdownCacheRef.current.delete(breakdownId);
    breakdownListenersRef.current.delete(breakdownId);
  }, []);

  // ==================== Scene 相关 ====================

  const getSceneCache = useCallback((sceneId: string): SceneDataCache | null => {
    return sceneCacheRef.current.get(sceneId) || null;
  }, []);

  const setSceneCache = useCallback(
    (sceneId: string, data: Partial<SceneDataCache>) => {
      const existingCache = sceneCacheRef.current.get(sceneId);
      const newCache: SceneDataCache = {
        sceneInfo: null,
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      sceneCacheRef.current.set(sceneId, newCache);

      notifyListeners(sceneListenersRef.current, sceneId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'scene',
        id: sceneId,
        action: 'update',
        timestamp: Date.now(),
      });

    },
    [notifyListeners, broadcastMessage]
  );

  const subscribeSceneCache = useCallback((sceneId: string, callback: () => void) => {
    if (!sceneListenersRef.current.has(sceneId)) {
      sceneListenersRef.current.set(sceneId, new Set());
    }
    sceneListenersRef.current.get(sceneId)!.add(callback);

    return () => {
      sceneListenersRef.current.get(sceneId)?.delete(callback);
    };
  }, []);

  const clearSceneCache = useCallback((sceneId: string) => {
    sceneCacheRef.current.delete(sceneId);
    sceneListenersRef.current.delete(sceneId);
  }, []);

  // ==================== Element 相关 ====================

  const getElementCache = useCallback((breakdownId: string): ElementDataCache | null => {
    return elementCacheRef.current.get(breakdownId) || null;
  }, []);

  const setElementCache = useCallback(
    (breakdownId: string, data: Partial<ElementDataCache>) => {
      const existingCache = elementCacheRef.current.get(breakdownId);
      const newCache: ElementDataCache = {
        elements: [],
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      elementCacheRef.current.set(breakdownId, newCache);

      notifyListeners(elementListenersRef.current, breakdownId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'element',
        id: breakdownId,
        action: 'update',
        timestamp: Date.now(),
      });
    },
    [notifyListeners, broadcastMessage]
  );

  const subscribeElementCache = useCallback((breakdownId: string, callback: () => void) => {
    if (!elementListenersRef.current.has(breakdownId)) {
      elementListenersRef.current.set(breakdownId, new Set());
    }
    elementListenersRef.current.get(breakdownId)!.add(callback);

    return () => {
      elementListenersRef.current.get(breakdownId)?.delete(callback);
    };
  }, []);

  const clearElementCache = useCallback((breakdownId: string) => {
    elementCacheRef.current.delete(breakdownId);
    elementListenersRef.current.delete(breakdownId);
  }, []);

  // ==================== ElementRelation 相关 ====================

  const getElementRelationCache = useCallback((breakdownId: string): ElementRelationDataCache | null => {
    return elementRelationCacheRef.current.get(breakdownId) || null;
  }, []);

  const setElementRelationCache = useCallback(
    (breakdownId: string, data: Partial<ElementRelationDataCache>) => {
      const existingCache = elementRelationCacheRef.current.get(breakdownId);
      const newCache: ElementRelationDataCache = {
        elementRelations: [],
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      elementRelationCacheRef.current.set(breakdownId, newCache);

      notifyListeners(elementRelationListenersRef.current, breakdownId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'elementRelation',
        id: breakdownId,
        action: 'update',
        timestamp: Date.now(),
      });
    },
    [notifyListeners, broadcastMessage]
  );

  const subscribeElementRelationCache = useCallback((breakdownId: string, callback: () => void) => {
    if (!elementRelationListenersRef.current.has(breakdownId)) {
      elementRelationListenersRef.current.set(breakdownId, new Set());
    }
    elementRelationListenersRef.current.get(breakdownId)!.add(callback);

    return () => {
      elementRelationListenersRef.current.get(breakdownId)?.delete(callback);
    };
  }, []);

  const clearElementRelationCache = useCallback((breakdownId: string) => {
    elementRelationCacheRef.current.delete(breakdownId);
    elementRelationListenersRef.current.delete(breakdownId);
  }, []);

  // ==================== Schedule 列表缓存（按 breakdownId）====================

  const getScheduleCache = useCallback((breakdownId: string): ScheduleDataCache | null => {
    return scheduleAggregateCacheRef.current.get(breakdownId) || null;
  }, []);

  const setScheduleCache = useCallback(
    (breakdownId: string, data: Partial<ScheduleDataCache>) => {
      const existingCache = scheduleAggregateCacheRef.current.get(breakdownId);
      const newCache: ScheduleDataCache = {
        schedules: [],
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      scheduleAggregateCacheRef.current.set(breakdownId, newCache);

      notifyListeners(scheduleListenersRef.current, breakdownId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'schedule',
        id: breakdownId,
        action: 'update',
        timestamp: Date.now(),
      });

    },
    [notifyListeners, broadcastMessage]
  );

  const subscribeScheduleCache = useCallback((breakdownId: string, callback: () => void) => {
    if (!scheduleListenersRef.current.has(breakdownId)) {
      scheduleListenersRef.current.set(breakdownId, new Set());
    }
    scheduleListenersRef.current.get(breakdownId)!.add(callback);

    return () => {
      scheduleListenersRef.current.get(breakdownId)?.delete(callback);
    };
  }, []);

  const clearScheduleCache = useCallback((breakdownId: string) => {
    scheduleAggregateCacheRef.current.delete(breakdownId);
    scheduleListenersRef.current.delete(breakdownId);
  }, []);

  /**
   * 全局只保留「当前」一套日程的 slot/date：移除其它拍摄计划的缓存条目与订阅
   */
  const pruneSlotAndDateCachesExcept = useCallback((keepScheduleId: string) => {
    if (!keepScheduleId || keepScheduleId.trim() === '') return;

    for (const key of Array.from(slotCacheRef.current.keys())) {
      if (key !== keepScheduleId) {
        slotCacheRef.current.delete(key);
        slotListenersRef.current.delete(key);
      }
    }
    for (const key of Array.from(calDateCacheRef.current.keys())) {
      if (key !== keepScheduleId) {
        calDateCacheRef.current.delete(key);
        calDateListenersRef.current.delete(key);
      }
    }
  }, []);

  // ==================== Slot 相关 ====================

  const getSlotCache = useCallback((scheduleId: string): SlotDataCache | null => {
    return slotCacheRef.current.get(scheduleId) || null;
  }, []);

  const setSlotCache = useCallback(
    (scheduleId: string, data: Partial<SlotDataCache>) => {
      const existingCache = slotCacheRef.current.get(scheduleId);
      const newCache: SlotDataCache = {
        scheduleInfo: null,
        slots: [],
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      slotCacheRef.current.set(scheduleId, newCache);

      notifyListeners(slotListenersRef.current, scheduleId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'slot',
        id: scheduleId,
        action: 'update',
        timestamp: Date.now(),
      });

      pruneSlotAndDateCachesExcept(scheduleId);
    },
    [notifyListeners, broadcastMessage, pruneSlotAndDateCachesExcept]
  );

  const subscribeSlotCache = useCallback((scheduleId: string, callback: () => void) => {
    if (!slotListenersRef.current.has(scheduleId)) {
      slotListenersRef.current.set(scheduleId, new Set());
    }
    slotListenersRef.current.get(scheduleId)!.add(callback);

    return () => {
      slotListenersRef.current.get(scheduleId)?.delete(callback);
    };
  }, []);

  const clearSlotCache = useCallback((scheduleId: string) => {
    slotCacheRef.current.delete(scheduleId);
    slotListenersRef.current.delete(scheduleId);
  }, []);

  // ==================== CalDate 相关 ====================

  const getCalDateCache = useCallback((scheduleId: string): CalDateDataCache | null => {
    return calDateCacheRef.current.get(scheduleId) || null;
  }, []);

  const setCalDateCache = useCallback(
    (scheduleId: string, data: Partial<CalDateDataCache>) => {
      const existingCache = calDateCacheRef.current.get(scheduleId);
      const newCache: CalDateDataCache = {
        calDates: [],
        timestamp: Date.now(),
        ...existingCache,
        ...data,
      };
      calDateCacheRef.current.set(scheduleId, newCache);

      notifyListeners(calDateListenersRef.current, scheduleId);

      broadcastMessage({
        type: 'CACHE_UPDATE',
        cacheType: 'calDate',
        id: scheduleId,
        action: 'update',
        timestamp: Date.now(),
      });

      pruneSlotAndDateCachesExcept(scheduleId);
    },
    [notifyListeners, broadcastMessage, pruneSlotAndDateCachesExcept]
  );

  const subscribeCalDateCache = useCallback((scheduleId: string, callback: () => void) => {
    if (!calDateListenersRef.current.has(scheduleId)) {
      calDateListenersRef.current.set(scheduleId, new Set());
    }
    calDateListenersRef.current.get(scheduleId)!.add(callback);

    return () => {
      calDateListenersRef.current.get(scheduleId)?.delete(callback);
    };
  }, []);

  const clearCalDateCache = useCallback((scheduleId: string) => {
    calDateCacheRef.current.delete(scheduleId);
    calDateListenersRef.current.delete(scheduleId);
  }, []);

  // ==================== 通用方法 ====================

  const clearAllCache = useCallback(() => {
    breakdownCacheRef.current.clear();
    sceneCacheRef.current.clear();
    elementCacheRef.current.clear();
    elementRelationCacheRef.current.clear();
    scheduleAggregateCacheRef.current.clear();
    slotCacheRef.current.clear();
    calDateCacheRef.current.clear();

    breakdownListenersRef.current.clear();
    sceneListenersRef.current.clear();
    elementListenersRef.current.clear();
    elementRelationListenersRef.current.clear();
    scheduleListenersRef.current.clear();
    slotListenersRef.current.clear();
    calDateListenersRef.current.clear();
  }, []);

  const value: BreakdownDataCacheContextType = {
    getBreakdownCache,
    setBreakdownCache,
    subscribeBreakdownCache,
    clearBreakdownCache,

    getSceneCache,
    setSceneCache,
    subscribeSceneCache,
    clearSceneCache,

    getElementCache,
    setElementCache,
    subscribeElementCache,
    clearElementCache,

    getElementRelationCache,
    setElementRelationCache,
    subscribeElementRelationCache,
    clearElementRelationCache,

    getScheduleCache,
    setScheduleCache,
    subscribeScheduleCache,
    clearScheduleCache,

    getSlotCache,
    setSlotCache,
    subscribeSlotCache,
    clearSlotCache,

    getCalDateCache,
    setCalDateCache,
    subscribeCalDateCache,
    clearCalDateCache,

    clearAllCache,
  };

  return <BreakdownDataCacheContext.Provider value={value}>{children}</BreakdownDataCacheContext.Provider>;
};
